import * as THREE from 'three';
import { ColoredObjectSchema } from './ColoredObject';
import { GlintEffect } from './glint';
import { SelectableObject } from './selectableObject';
import { stringSchema } from '@/data/common';
import z from 'zod';

export const SlopeSchema = ColoredObjectSchema.extend({
  name: stringSchema.default('Slope'),
});

export class Slope extends SelectableObject<typeof SlopeSchema> {
  static override schema = SlopeSchema;

  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly glintEffect: GlintEffect;

  constructor(options: z.input<typeof SlopeSchema> = {}) {
    super(options);

    this.material = new THREE.MeshStandardMaterial({
      color: this.data.color,
      emissive: 0x000000,
      roughness: 0.35,
      metalness: 0.05,
    });

    const shape = new THREE.Shape();
    shape.moveTo(-0.5, -0.5);
    shape.lineTo(0.5, 0.5);
    shape.lineTo(-0.5, 0.5);
    shape.closePath();

    const prism = new THREE.ExtrudeGeometry(shape, {
      depth: 1,
      bevelEnabled: false,
      steps: 1,
    });

    prism.rotateX(Math.PI);
    prism.translate(0, 0, 0.5);
    prism.rotateY(Math.PI / 2);

    this.mesh = new THREE.Mesh(prism, this.material);

    this.glintEffect = new GlintEffect(this.material);
  }

  protected renderObject(parent: THREE.Object3D) {
    parent.add(this.mesh);
  }

  override getPickTargets() {
    return [this.mesh];
  }

  override getAabb() {
    return new THREE.Box3().setFromCenterAndSize(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(1, 1, 1),
    );
  }

  override update(delta: number): void {
    this.glintEffect.update(delta);
  }

  override dispose() {
    super.dispose();
    this.glintEffect.dispose();
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  protected override onSelectionChanged(selected: boolean): void {
    this.glintEffect.setSelected(selected);
  }
}
